import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { object, string, array, bool, number, func } from 'prop-types';
import { getGenericRow } from 'ps-components/AdvancedTable/GenericGrid/GenericGridFunctions';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import addAlert from 'ps-components/AlertToast/addAlert';
import { jobMatchGridActions } from '../../LandingPage/JobMatchDetail/JobMatchGridCreate';

import GG_DEFAULTS from '../../../../lib/genericGridDefaults';
import './SlottedGrid.scss';

SlottedGrid.propTypes = {
  history: object,
  masterAccountId: string,
  slottedSearchGrid: object,
  slottedSearchGridActions: object,
  readOnlyUser: bool,
  restrictedUser: bool,
  offset: number,
  useJobCodeKey: bool,
  jobCodeKeyText: string,
  loading: bool,
  searchQuery: string,
  loadingId: number,
  selectedRowId: string,
  isMatchDeleting: bool,
  selectedSlottedJobs: array,
  addMatch: func,
  deleteMatch: func,
  currentJob: object,
  activeBenchmarkYear: number,
  selectedBenchmarkYear: number,
  updateModifiedDate: func,
  onMatchSelect: func,
  refreshJobTabData: func,
  hideSelect: bool,
  aging: string,
  numericRounding: number,
  percentRounding: number
};

function SlottedGrid(props) {
  const [addedMatches, setAddedMatches] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const dispatch = useDispatch();

  function isRowLoading(row, loadingId) {
    return loadingId === row.allCells.find(cell => cell.column.id === GG_DEFAULTS.ORG_JOB_ID)?.value;
  }

  function useSelectAccessor(el) {
    return el.allCells.find(cell => cell.column.id === GG_DEFAULTS.ORG_JOB_ID)?.value;
  }

  function isChecked(orgJobId) {
    return addedMatches.includes(orgJobId) || props.selectedSlottedJobs.includes(orgJobId);
  }

  function getCheckBoxTooltipText(el) {
    const rowAgingCode = el?.values?.aging_code_default || el?.values?.aging_code || '';

    if (props.aging !== undefined && rowAgingCode !== props.aging) {
      return `Cannot match aging code "${rowAgingCode}" to current code: "${props.aging}"`;
    }

    return null;
  }

  function useDisabledAccessor(el) {
    return el.allCells.find(cell => cell.column.id === GG_DEFAULTS.AGING_CODE)?.value;
  }

  function isSelectDisabled(rowAgingCode) {
    return props.aging !== undefined && rowAgingCode !== props.aging;
  }

  function setSelected(selectedId) {
    const {
      currentJob,
      selectedBenchmarkYear,
      onMatchSelect,
      slottedSearchGrid,
      selectedSlottedJobs,
      updateModifiedDate,
      refreshJobTabData,
      hideSelect,
      aging
    } = props;

    if (hideSelect) return;

    const newSetValue = !selectedSlottedJobs?.includes(selectedId);
    const orgJobIdIndex = slottedSearchGrid.terms.findIndex(term => term.term === GG_DEFAULTS.ORG_JOB_ID);
    const orgJobCodeIndex = slottedSearchGrid.terms.findIndex(term => term.term === GG_DEFAULTS.ORG_JOB_CODE);
    const orgJobCodeKeyIndex = slottedSearchGrid.terms.findIndex(term => term.term === GG_DEFAULTS.ORG_JOB_CODE_KEY);
    const agingCodeIndex = slottedSearchGrid.terms.findIndex(term => term.term === GG_DEFAULTS.AGING_CODE);
    const currencyIdIndex = slottedSearchGrid.terms.findIndex(term => term.term === GG_DEFAULTS.CURRENCY);

    const foundItem = slottedSearchGrid.data.find(el => el[orgJobIdIndex] === selectedId);

    const slottedAgingCode = foundItem[agingCodeIndex];
    const currentAgingCode = selectedSlottedJobs?.length === 0 ? '' : aging;

    if (currentAgingCode && currentAgingCode?.toLowerCase() !== slottedAgingCode?.toLowerCase()) {
      addAlert(`Cannot match aging code "${slottedAgingCode}" to current code: "${currentAgingCode}"`, 'warning');
    } else {
      setIsSaving(true);
      handleSetAction(
        newSetValue,
        foundItem[orgJobIdIndex],
        foundItem[orgJobCodeIndex],
        foundItem[orgJobCodeKeyIndex],
        slottedAgingCode
      )
        .then(() => {
          updateModifiedDate(currentJob.id, selectedBenchmarkYear);
          const newAddedMatches = addedMatches;

          if (newSetValue === true) {
            newAddedMatches.push(selectedId);
          } else {
            newAddedMatches.splice(newAddedMatches.indexOf(selectedId), 1);
          }

          setAddedMatches(newAddedMatches);

          if (onMatchSelect) {
            onMatchSelect(foundItem[currencyIdIndex], newSetValue !== true);
          }
          setIsSaving(false);
          refreshJobTabData();
        })
        .catch(() => {
          setIsSaving(false);
        });
    }
  }

  function handleSetAction(newSetValue, jobId, jobCode, jobCodeKey, agingCode) {
    const { currentJob, addMatch, deleteMatch, selectedBenchmarkYear } = props;
    dispatch(jobMatchGridActions.setIsStale(currentJob.id, true));
    if (newSetValue === true) {
      return addMatch(currentJob.id, undefined, undefined, false, selectedBenchmarkYear, {
        slottedOrgJobId: jobId,
        slottedOrgJobCode: jobCode,
        slottedOrgJobCodeKey: jobCodeKey,
        slottedAgingCode: agingCode
      });
    } else {
      return deleteMatch(currentJob.id, undefined, undefined, selectedBenchmarkYear, false, {
        slottedOrgJobId: jobId,
        slottedOrgJobCode: jobCode,
        slottedOrgJobCodeKey: jobCodeKey
      });
    }
  }

  return (
    <div className="generic-grid__container slotted-search__grid-container">
      <AdvancedTable
        {...props.slottedSearchGrid.tableProps}
        {...props.slottedSearchGridActions}
        getDataRow={(row, idx) => {
          return getGenericRow(row, idx, {
            onClick: null,
            useJobCodeKey: props.useJobCodeKey,
            jobCodeKeyText: props.jobCodeKeyText,
            isRowLoading: isRowLoading(row, props.loadingId),
            isRowDeleting: props.isMatchDeleting,
            loadingText: 'Adding Match to Pricing...',
            deletingText: 'Removing Match from Pricing...',
            addedMatches: addedMatches,
            numericRounding: props.numericRounding,
            percentRounding: props.percentRounding
          });
        }}
        stickyHeader
        useSelectAll={false}
        dataLoading={props.loading}
        loadingText={'Loading Job Data'}
        onRowClick={el => {}}
        useSelectAccessor={useSelectAccessor}
        useDisabledAccessor={useDisabledAccessor}
        isChecked={isChecked}
        onCheck={props.hideSelect || isSaving ? null : setSelected}
        hideSelect={props.hideSelect}
        noDataText={'No Jobs Found'}
        loadingId={props.loadingId}
        isSelectDisabled={isSelectDisabled}
        getCheckBoxTooltipText={getCheckBoxTooltipText}
      />
    </div>
  );
}

export default SlottedGrid;
